import { createSlice } from '@reduxjs/toolkit';
import { logout, refreshToken } from '../slices/portalAuth';
import { logger } from '../../helpers/logger';
import { BroadcastEventType, DialogTypes } from '../../enums';
import { commonService } from '../../services/commonServiceInstance';
import { handleApiCall } from '../../helpers/handleApiCall';
import { broadcastChannel } from '../../broadcast/BroadcastChannel';
import { removeSpecificDialog } from '../../components/dialogs/DialogRoot/redux';
import isEqual from 'lodash/isEqual';

const COUNTDOWN_SECONDS = 60;

const initialState = {
    errorMessageDialogIsVisible: false,
    autoLogoutDialogIsVisible: false,
    facilities: null,
    facilitiesByPermission: {},
    facilitiesWithMarket: null,
    siteAlert: {},
};

const slice = createSlice({
    name: 'general',
    initialState,
    reducers: {
        setAutoLogoutDialogVisible(state, action) {
            state.autoLogoutDialogIsVisible = action.payload;
        },
        setAutoLogoutMessage(state, action) {
            state.autoLogoutMessage = action.payload;
        },
        setFacilities(state, action) {
            state.facilities = action.payload;
        },
        setFacilitiesByPermission(state, action) {
            const { facilities, requiredPermission } = action.payload;
            state.facilitiesByPermission[requiredPermission] = facilities;
        },
        setFacilitiesWithMarket(state, action) {
            state.facilitiesWithMarket = action.payload;
        },
        setSiteAlert(state, action) {
            state.siteAlert = action.payload;
        },
    },
});

export const {
    setAutoLogoutDialogVisible,
    setAutoLogoutMessage,
    setAutoLogoutCountdownHandler,
    setFacilities,
    setIsLoadingFacilities,
    setFacilitiesByPermission,
    setFacilitiesWithMarket,
    setSiteAlert,
} = slice.actions;
export const general = { reducer: slice.reducer, initialState };
export default general;

// Thunks (side effects)
export const showAutoLogoutDialog = () => async (dispatch, getState) => {
    const { autoLogoutDialogIsVisible } = getState().general;
    if (!autoLogoutDialogIsVisible) {
        startAutoLogoutCountdown(dispatch, COUNTDOWN_SECONDS);
    }
};

export const cancelAutoLogoutDialog = () => async (dispatch) => {
    await dispatch(clearAutoLogoutDialog());
    // tell other tabs to cancel auto logout
    broadcastChannel.postMessage({ type: BroadcastEventType.CancelAutoLogout });
};

export const clearAutoLogoutDialog = (preventTokenRefresh) => async (dispatch) => {
    dispatch(removeSpecificDialog(DialogTypes.AutoLogoutDialog));

    if (!preventTokenRefresh) {
        //refresh token so the countdown aligns with the new token expiry
        dispatch(refreshToken());
    }
};

export const performAutoLogout = (reason) => async (dispatch) => {
    dispatch(logout(reason));
};

export const loadFacilities = () => async (dispatch, getState) => {
    const { facilities } = getState().general;

    if (facilities) return;

    const result = await handleApiCall(
        async () => {
            return await commonService.getFacilityHierarchies({
                includeMarkets: true,
            });
        },
        { dispatch },
    );

    if (result.wasSuccessful && result) {
        dispatch(setFacilities(result));
    }
};

const loadingFacilities = new WeakMap();

export const getFacilityHierarchies =
    ({ requiredPermission, includeMarkets, componentId }) =>
    async (dispatch, getState) => {
        const { facilities, facilitiesByPermission, facilitiesWithMarket } = getState().general;
        const loadKey = getFacilitiesLoadKey(includeMarkets, requiredPermission);

        //TODO: build in case for include markets AND add support for filtering by permission
        if (requiredPermission && includeMarkets)
            throw new Error('Cannot include markets when filtering by permission');

        const cachedFacilities = requiredPermission
            ? facilitiesByPermission[requiredPermission]
            : includeMarkets
              ? facilitiesWithMarket
              : facilities;
        if (cachedFacilities) return cachedFacilities;

        if (!loadingFacilities.has(getState)) loadingFacilities.set(getState, {});
        const pendingRequests = loadingFacilities.get(getState);
        if (pendingRequests[loadKey]) return pendingRequests[loadKey];

        const data = {
            includeMarkets: includeMarkets ?? false,
            requiredPermission: requiredPermission ?? undefined,
        };

        const request = handleApiCall(
            async () => await commonService.getFacilityHierarchies(data),
            {
                dispatch,
                componentId,
                errorMessage: 'Failed to load facilities. Please wait a moment, and try again.',
            },
        )
            .then((result) => {
                if (result.wasSuccessful) {
                    if (requiredPermission) {
                        dispatch(
                            setFacilitiesByPermission({ requiredPermission, facilities: result }),
                        );
                    } else {
                        if (includeMarkets) dispatch(setFacilitiesWithMarket(result));
                        dispatch(setFacilities(result));
                    }
                }
                return result;
            })
            .finally(() => {
                delete pendingRequests[loadKey];
            });

        pendingRequests[loadKey] = request;
        return request;
    };

export const checkForSiteAlert = (url) => async (dispatch, getState) => {
    let result;
    try {
        result = await commonService.getSiteAlert(url);
    } catch (error) {
        logger.error('Error fetching site alert', error);
    }

    if (!result) {
        dispatch(setSiteAlert({}));
        return;
    }

    const { clientIds, enabled, id } = result;
    const allocatedClientIds = getState().auth?.allocatedClientIds;
    const hasClientIntersection =
        !clientIds?.length || clientIds?.some((id) => allocatedClientIds?.includes(id));

    const nextSiteAlert = enabled && id && hasClientIntersection ? result : {};
    if (!isEqual(nextSiteAlert, getState().general?.siteAlert)) {
        dispatch(setSiteAlert(nextSiteAlert));
    }
};

//Helpers
export const getFacilitiesLoadKey = (includeMarkets, requiredPermission) =>
    `${includeMarkets ? 'WithMarket' : 'WithoutMarket'}${
        requiredPermission ? `-PermissionSet:${requiredPermission}` : ''
    }`;
